import { supabase } from '../lib/supabase'
import type { WriteResult } from './projectWrites'
import type { Deliverable } from '../types/portal'

/**
 * PUTTING IT UP AND TELLING THEM, AS ONE ACTION.
 *
 * This is the actual Bloomberg failure. Tom: *"it was getting messy to put
 * the next one up. Because they couldn't see it."* Marking a cut ready and
 * telling the client were two separate jobs, so the second one did not always
 * happen and a finished episode sat on a page nobody had been given a reason
 * to open.
 *
 * Three parts, all here:
 *   WHO    `project_client_contacts` — the people to tell, per project.
 *          Staff-only RLS: a client holding the page link cannot read their
 *          own colleagues' addresses out of it.
 *   WHAT   a drafted line, written for you, editable before it goes.
 *   TOLD   which pieces have already been announced, read back from the job
 *          queue rather than stored again. One fewer thing to keep true.
 *
 * The send itself is `queue_client_ready`, a SECURITY DEFINER function that
 * refuses anyone who is not studio staff, validates and caps the recipients,
 * and queues a `client_ready` job. `send-notifications` picks it up within
 * seconds and sends one email each — never a shared To line, because a
 * client's people may sit at different companies.
 */

export interface ClientContact {
  id: string
  name: string
  email: string
}

function fail(error: { message: string; code?: string } | null, what: string): WriteResult {
  if (!error) return { ok: true }
  const detail = `${what}: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] write refused — ${detail}`)
  return { ok: false, error: detail }
}

export async function fetchClientContacts(projectId: string): Promise<ClientContact[]> {
  if (!supabase || !projectId) return []
  const { data, error } = await supabase
    .from('project_client_contacts')
    .select('id, name, email')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true })

  if (error) {
    console.warn(`[supabase] client contacts load failed: ${error.message}`)
    return []
  }
  return (data ?? []).map((row) => ({
    id: String((row as Record<string, unknown>).id ?? ''),
    name: String((row as Record<string, unknown>).name ?? ''),
    email: String((row as Record<string, unknown>).email ?? ''),
  }))
}

export async function addClientContact(
  projectId: string,
  name: string,
  email: string,
): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const { error } = await supabase
    .from('project_client_contacts')
    .insert({ project_id: projectId, name: name.trim(), email: email.trim().toLowerCase() })
  return fail(error, 'project_client_contacts')
}

export async function removeClientContact(id: string): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const { error } = await supabase.from('project_client_contacts').delete().eq('id', id)
  return fail(error, 'project_client_contacts')
}

/** Loose on purpose — the real check is the database's. This only catches a typo. */
export function looksLikeEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim())
}

/**
 * Which pieces the client has already been told about, and when.
 *
 * Read out of the job queue rather than stamped onto the deliverable: the
 * queue already knows, and a second place to record it is a second place to
 * be wrong. A job still pending counts as told — it is going out within
 * seconds and the one thing worse than not telling them is telling them
 * twice.
 */
export async function fetchTold(projectId: string): Promise<Map<string, string>> {
  const told = new Map<string, string>()
  if (!supabase || !projectId) return told

  const { data, error } = await supabase
    .from('staff_notification_jobs')
    .select('payload, status, requested_at, sent_at')
    .eq('project_id', projectId)
    .eq('event_type', 'client_ready')

  if (error) {
    console.warn(`[supabase] told-already load failed: ${error.message}`)
    return told
  }

  for (const row of data ?? []) {
    const record = row as Record<string, unknown>
    if (String(record.status ?? '') === 'error') continue
    const payload = (record.payload ?? {}) as Record<string, unknown>
    const key = String(payload.pieceKey ?? '')
    if (!key) continue
    const when = String(record.sent_at ?? record.requested_at ?? '').slice(0, 10)
    const already = told.get(key) ?? ''
    if (!already || when > already) told.set(key, when)
  }
  return told
}

/** The subject, written for you. */
export function draftSubject(piece: Deliverable): string {
  if (piece.type === 'Photography') return `${piece.name} is ready to look at`
  if (piece.type === 'Audio') return `${piece.name} is ready to listen to`
  return `${piece.name} is ready to watch`
}

/**
 * The line, written for you.
 *
 * Short on purpose. The email itself already explains where to approve and
 * how to ask for a change — that is in the template, the same every time, so
 * saying it again here would only crowd out the half a producer actually
 * wants to write.
 */
export function draftBody(piece: Deliverable, first: boolean): string {
  const verb =
    piece.type === 'Photography' ? 'to look through' : piece.type === 'Audio' ? 'to listen to' : 'to watch'
  if (first) {
    return `${piece.name} is up on your page now — the first thing you'll see when you open it, and ready ${verb}.`
  }
  return `${piece.name} is up on your page now. It leads the page, and everything before it has moved down.`
}

/** Queue it. The worker sends within seconds. */
export async function tellThem(
  projectId: string,
  deliverableKey: string,
  subject: string,
  body: string,
  recipients: string[],
): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const { error } = await supabase.rpc('queue_client_ready', {
    p_project_id: projectId,
    p_deliverable_key: deliverableKey,
    p_subject: subject,
    p_body: body,
    p_recipients: recipients,
  })
  return fail(error, 'queue_client_ready')
}
